/**
 * Push Notifications Page (Agent 94)
 * - Compose form: title, body, deeplink, cohort dropdown
 * - Recent broadcasts list (timestamp, sent/recipient counts, status)
 * - AI Push Frequency settings tab (admin_settings)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import './PushNotificationsPage.css';

type CohortType = 'all' | 'verified' | 'pending_kyc' | 'inactive_7d' | 'high_value';
type Tab = 'broadcasts' | 'ai-frequency';

const COHORT_OPTIONS: Array<{ value: CohortType; label: string; hint?: string }> = [
  { value: 'all', label: 'All users (with push subscription)' },
  { value: 'verified', label: 'Verified users', hint: 'kyc_status: approved or auto_verified' },
  { value: 'pending_kyc', label: 'Pending KYC', hint: 'kyc_status: pending, manual_review, pending_full_kyc' },
  { value: 'inactive_7d', label: 'Inactive 7+ days', hint: 'No app session in last 7 days' },
  {
    value: 'high_value',
    label: 'High-value (deposited ≥ 500)',
    hint: 'Sum of deposits ≥ 500 in user.currency. No FX conversion — same threshold for all currencies.',
  },
];

interface Broadcast {
  id: string;
  title: string;
  body: string;
  deeplink: string | null;
  imageUrl: string | null;
  cohort: string;
  status: string;
  recipientCount: number;
  sentCount: number;
  errorMessage: string | null;
  createdAt: string;
  sentAt: string | null;
  adminEmail: string | null;
}

interface AiSettings {
  ai_push_frequency_enabled: boolean;
  ai_push_time_of_day: 'morning' | 'afternoon' | 'evening';
  ai_push_triggered_inactive: boolean;
  ai_push_triggered_settled: boolean;
  ai_push_max_per_day: 1 | 2 | 3 | 5;
}

const PushNotificationsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('broadcasts');

  // Compose form
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [deeplink, setDeeplink] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [cohort, setCohort] = useState<CohortType>('verified');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Recent broadcasts list
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // AI settings tab
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);

  const loadBroadcasts = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const r = await apiService.listPushBroadcasts({ limit: 50 });
      setBroadcasts(r.broadcasts);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load broadcasts');
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const s = await apiService.getAiPushSettings();
      setSettings(s);
    } catch (err) {
      setSettingsMsg(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'broadcasts') void loadBroadcasts();
    if (tab === 'ai-frequency' && !settings) void loadSettings();
  }, [tab, loadBroadcasts, loadSettings, settings]);

  // B4 rich push: upload the campaign image (S3 via the shared promo-asset
  // endpoint) and keep only the resulting https URL in the form state.
  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setSubmitMsg(null);
    try {
      const { url } = await apiService.uploadPromoAsset(file);
      setImageUrl(url);
    } catch (err) {
      setSubmitMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Image upload failed',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitMsg(null);
    if (!title.trim() || !bodyText.trim()) {
      setSubmitMsg({ kind: 'err', text: 'Title and body are required.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiService.createPushBroadcast({
        title: title.trim(),
        body: bodyText.trim(),
        deeplink: deeplink.trim() || undefined,
        imageUrl: imageUrl || undefined,
        cohort: { type: cohort },
      });
      setSubmitMsg({
        kind: 'ok',
        text: `Queued broadcast ${res.id.slice(0, 8)} → ${res.recipient_count} recipients.`,
      });
      setTitle('');
      setBodyText('');
      setDeeplink('');
      setImageUrl('');
      void loadBroadcasts();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send broadcast';
      setSubmitMsg({ kind: 'err', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const updateSetting = async <K extends keyof AiSettings>(key: K, value: AiSettings[K]) => {
    if (!settings) return;
    const next = { ...settings, [key]: value };
    setSettings(next);
    setSettingsSaving(true);
    setSettingsMsg(null);
    try {
      await apiService.updateAiPushSettings({ [key]: value } as Partial<AiSettings>);
      setSettingsMsg('Saved.');
    } catch (err) {
      setSettingsMsg(err instanceof Error ? err.message : 'Save failed');
      // revert optimistic update
      setSettings(settings);
    } finally {
      setSettingsSaving(false);
    }
  };

  return (
    <div className="push-notifications-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Push Notifications</h1>
          <p className="page-sub">Compose broadcasts and tune AI personalization</p>
        </div>
        <div className="tab-bar">
          <button
            className={`tab ${tab === 'broadcasts' ? 'active' : ''}`}
            onClick={() => setTab('broadcasts')}
          >
            Broadcasts
          </button>
          <button
            className={`tab ${tab === 'ai-frequency' ? 'active' : ''}`}
            onClick={() => setTab('ai-frequency')}
          >
            AI Push Frequency
          </button>
        </div>
      </div>

      {tab === 'broadcasts' && (
        <div className="broadcasts-tab">
          <div className="card compose-card">
            <h2>Compose</h2>
            <form onSubmit={handleSubmit} className="compose-form">
              <div className="field">
                <label htmlFor="pn-title">Title</label>
                <input
                  id="pn-title"
                  type="text"
                  maxLength={120}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Big news for you"
                  disabled={submitting}
                />
                <span className="counter">{title.length}/120</span>
              </div>

              <div className="field">
                <label htmlFor="pn-body">Body</label>
                <textarea
                  id="pn-body"
                  maxLength={500}
                  rows={3}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Tap to see what's new"
                  disabled={submitting}
                />
                <span className="counter">{bodyText.length}/500</span>
              </div>

              <div className="field">
                <label htmlFor="pn-deeplink">Deeplink (optional)</label>
                <input
                  id="pn-deeplink"
                  type="text"
                  maxLength={500}
                  value={deeplink}
                  onChange={(e) => setDeeplink(e.target.value)}
                  placeholder="versus://markets/abc123"
                  disabled={submitting}
                />
              </div>

              <div className="field">
                <label htmlFor="pn-image">Image (optional — rich push)</label>
                <input
                  id="pn-image"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={submitting || uploading}
                  onChange={handleImageFile}
                />
                {uploading && <span className="hint">Uploading…</span>}
                {imageUrl && !uploading && (
                  <div className="pnx-image-preview">
                    <img src={imageUrl} alt="Push campaign" />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setImageUrl('')}
                    >
                      Remove
                    </button>
                  </div>
                )}
                <span className="hint">
                  Rendered large in the notification (iOS rich push / Android). Uploaded to S3.
                </span>
              </div>

              <div className="field">
                <label htmlFor="pn-cohort">Cohort</label>
                <select
                  id="pn-cohort"
                  value={cohort}
                  onChange={(e) => setCohort(e.target.value as CohortType)}
                  disabled={submitting}
                >
                  {COHORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                  <option value="ai-cohort" disabled>
                    AI-generated cohort (coming soon)
                  </option>
                </select>
                <span className="hint">
                  {COHORT_OPTIONS.find((o) => o.value === cohort)?.hint || ''}
                </span>
              </div>

              <div className="field">
                <label>
                  <span>Schedule (coming soon)</span>
                  <input
                    type="datetime-local"
                    disabled
                    title="Scheduling not yet implemented — push fires immediately."
                  />
                </label>
              </div>

              <div className="actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || !title.trim() || !bodyText.trim()}
                >
                  {submitting ? 'Queuing…' : 'Send broadcast'}
                </button>
                {submitMsg && (
                  <span className={`submit-msg ${submitMsg.kind}`}>{submitMsg.text}</span>
                )}
              </div>
            </form>
          </div>

          <div className="card pnx-preview-card">
            <h2>Preview</h2>
            <p className="card-sub">How the notification lands on the lock screen.</p>
            <div className="pnx-preview">
              <div className="pnx-preview-head">
                <div className="pnx-preview-appicon">V</div>
                <span className="pnx-preview-app">VERSUS</span>
                <span className="pnx-preview-time">now</span>
              </div>
              <div className="pnx-preview-title">{title || 'Notification title'}</div>
              <div className="pnx-preview-body">{bodyText || 'Notification body appears here.'}</div>
              {imageUrl && <img className="pnx-preview-image" src={imageUrl} alt="" />}
              {deeplink && <div className="pnx-preview-deeplink">↗ {deeplink}</div>}
            </div>
          </div>

          <div className="card list-card">
            <div className="list-head">
              <h2>Recent broadcasts</h2>
              <button className="btn btn-secondary btn-sm" onClick={loadBroadcasts}>
                Refresh
              </button>
            </div>
            {listError && <div className="error">{listError}</div>}
            {listLoading ? (
              <div className="loading">Loading…</div>
            ) : broadcasts.length === 0 ? (
              <div className="empty">No broadcasts yet.</div>
            ) : (
              <table className="broadcasts-table">
                <thead>
                  <tr>
                    <th>Sent at</th>
                    <th>Title</th>
                    <th>Image</th>
                    <th>Cohort</th>
                    <th>Status</th>
                    <th>Recipients</th>
                    <th>Delivered</th>
                    <th>Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {broadcasts.map((b) => (
                    <tr key={b.id} title={b.errorMessage || ''}>
                      <td className="ts">{new Date(b.createdAt).toLocaleString()}</td>
                      <td className="title-cell">
                        <div className="b-title">{b.title}</div>
                        <div className="b-body">{b.body}</div>
                      </td>
                      <td>
                        {b.imageUrl ? (
                          <img className="pnx-b-thumb" src={b.imageUrl} alt="" />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <code>{b.cohort}</code>
                      </td>
                      <td>
                        <span className={`status-pill ${b.status}`}>{b.status}</span>
                      </td>
                      <td>{b.recipientCount}</td>
                      <td>{b.sentCount}</td>
                      <td className="admin-cell">{b.adminEmail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'ai-frequency' && (
        <div className="ai-tab">
          <div className="card">
            <h2>AI Push Frequency</h2>
            <p className="card-sub">
              Controls the AI personalization layer. Settings persist in <code>admin_settings</code>.
            </p>
            {settingsLoading || !settings ? (
              <div className="loading">Loading settings…</div>
            ) : (
              <div className="settings-grid">
                <div className="setting-row">
                  <div className="setting-label">
                    <strong>Daily personalized push</strong>
                    <span className="hint">Master switch for AI-generated daily push.</span>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.ai_push_frequency_enabled}
                      onChange={(e) =>
                        updateSetting('ai_push_frequency_enabled', e.target.checked)
                      }
                    />
                    <span className="slider" />
                  </label>
                </div>

                <div className="setting-row">
                  <div className="setting-label">
                    <strong>Time of day</strong>
                    <span className="hint">When the daily push fires (user local time).</span>
                  </div>
                  <select
                    value={settings.ai_push_time_of_day}
                    onChange={(e) =>
                      updateSetting(
                        'ai_push_time_of_day',
                        e.target.value as AiSettings['ai_push_time_of_day']
                      )
                    }
                  >
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                  </select>
                </div>

                <div className="setting-row">
                  <div className="setting-label">
                    <strong>Triggered: 24h-no-login</strong>
                    <span className="hint">Re-engagement push if user hasn't opened app in 24h.</span>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.ai_push_triggered_inactive}
                      onChange={(e) =>
                        updateSetting('ai_push_triggered_inactive', e.target.checked)
                      }
                    />
                    <span className="slider" />
                  </label>
                </div>

                <div className="setting-row">
                  <div className="setting-label">
                    <strong>Triggered: bet just settled</strong>
                    <span className="hint">Push when a user's bet resolves.</span>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.ai_push_triggered_settled}
                      onChange={(e) =>
                        updateSetting('ai_push_triggered_settled', e.target.checked)
                      }
                    />
                    <span className="slider" />
                  </label>
                </div>

                <div className="setting-row">
                  <div className="setting-label">
                    <strong>Max per day</strong>
                    <span className="hint">Hard cap across all AI push channels per user.</span>
                  </div>
                  <select
                    value={settings.ai_push_max_per_day}
                    onChange={(e) =>
                      updateSetting(
                        'ai_push_max_per_day',
                        Number(e.target.value) as AiSettings['ai_push_max_per_day']
                      )
                    }
                  >
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                  </select>
                </div>
              </div>
            )}
            {settingsMsg && (
              <div className={`settings-msg ${settingsSaving ? 'saving' : ''}`}>
                {settingsSaving ? 'Saving…' : settingsMsg}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PushNotificationsPage;
